import { type FC, type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type CommentResolutionFilterOption, type CommentVisibilityFilterOption } from '@/types/comments';
import { CommentsComposer } from '@/components/inspector/comments/comments-composer';
import { CommentsToolbar } from '@/components/inspector/comments/comments-toolbar';
import { useNodeCommentsChannel } from '@/hooks/comments/useNodeCommentsChannel';
import { CommentsList } from '@/components/inspector/comments/comments-list';
import { useNodeCommentList } from '@/hooks/comments/useNodeCommentList';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useCapabilities } from '@/hooks/useCapabilities';
import { type Asset } from '@/types/asset';
import { useRouter } from 'next/router';

const CommentsChannelSubscription: FC<{
  nodeId: string;
  hasNextPage: boolean;
  onNewComment: (commentId: string, insertPosition: 'top' | 'bottom') => void;
  sortOrder: SortOrderOptions;
  sortValue: SortOrderValues;
  resolutionFilter?: CommentResolutionFilterOption;
  visibilityFilter?: CommentVisibilityFilterOption;
  scrollContainerRef?: MutableRefObject<HTMLDivElement | null>;
}> = ({
  nodeId,
  hasNextPage,
  onNewComment,
  sortOrder,
  sortValue,
  resolutionFilter,
  visibilityFilter,
  scrollContainerRef,
}) => {
  useNodeCommentsChannel({
    nodeId,
    hasNextPage,
    sortOrder,
    sortValue,
    resolutionFilter,
    visibilityFilter,
    onNewComment,
    scrollContainerRef,
  });

  return null;
};

export const CommentsPanel: FC<{
  asset?: Asset;
  readOnly?: boolean;
  enabled: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}> = ({ asset, readOnly = false, enabled, disabled: _disabled, isLoading: _isLoading }) => {
  const [commentSortOrder, setCommentSortOrder] = useState<SortOrderOptions>(SortOrderOptions.DESC);
  const [commentSortValue, setCommentSortValue] = useState<SortOrderValues>(SortOrderValues.CREATED_AT);
  const [commentResolutionFilter, setCommentResolutionFilter] = useState<CommentResolutionFilterOption | undefined>(
    undefined,
  );
  const [commentVisibilityFilter, setCommentVisibilityFilter] = useState<CommentVisibilityFilterOption | undefined>(
    undefined,
  );

  const { query } = useRouter();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const wasAtTopRef = useRef(true);
  const wasAtBottomRef = useRef(false);
  const targetCommentIdRef = useRef<string | null>(null);

  const selectedCommentId = useMemo(
    () => (typeof query.commentId === 'string' ? query.commentId : undefined),
    [query.commentId],
  );
  const selectedReplyId = useMemo(
    () => (typeof query.replyId === 'string' ? query.replyId : undefined),
    [query.replyId],
  );

  const { canCommentPrivately } = useCapabilities();
  const shareConfig = useShareConfig();
  // The share-scoped comment list endpoint always excludes private comments and doesn't
  // support this filter at all, so it's only meaningful outside of a share.
  const canFilterByVisibility = canCommentPrivately && !shareConfig?.id;

  const {
    data: { comments = [] } = {},
    commentsArePending,
    commentsAreFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNodeCommentList(
    asset?.nodeId,
    {
      pagination: {
        initialLimit: 25,
        nextPageLimit: 10,
        offset: 0,
      },
      sort: {
        order: commentSortOrder,
        value: commentSortValue,
      },
      filter: {
        resolution: commentResolutionFilter,
        visibility: commentVisibilityFilter,
      },
      selected: selectedCommentId,
      shareId: shareConfig?.id,
    },
    {
      enabled: enabled && Boolean(asset?.nodeId),
    },
  );

  // Reset filters when asset changes
  useEffect(() => {
    setCommentResolutionFilter(undefined);
    setCommentVisibilityFilter(undefined);
  }, [asset?.nodeId]);

  // Reset filters if we move to a different inspector tab
  useEffect(() => {
    if (!enabled) {
      setCommentResolutionFilter(undefined);
      setCommentVisibilityFilter(undefined);
    }
  }, [enabled]);

  // Track if user is near top or bottom of scroll area
  // Using a larger threshold to account for partial visibility
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      // Consider "at top" if scrolled less than ~150px (roughly one comment height)
      wasAtTopRef.current = scrollTop < 150;
      // Consider "at bottom" if within ~150px of the bottom
      wasAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 150;
    }
  }, []);

  // Handle new comment from WebSocket
  const handleNewComment = useCallback((commentId: string, insertPosition: 'top' | 'bottom') => {
    if (insertPosition === 'top' && wasAtTopRef.current && scrollContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated with new comment
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else if (insertPosition === 'bottom' && wasAtBottomRef.current && scrollContainerRef.current) {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const targetId = selectedReplyId ?? selectedCommentId;
    if (!targetId || comments.length === 0 || targetCommentIdRef.current === targetId) {
      return;
    }

    const element =
      document.getElementById(targetId) ?? (selectedCommentId ? document.getElementById(selectedCommentId) : null);
    if (!element) {
      return;
    }

    requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    targetCommentIdRef.current = targetId;
  }, [comments.length, enabled, selectedCommentId, selectedReplyId]);

  return (
    <div className="flex h-full flex-col space-y-px bg-ui-container">
      <CommentsToolbar
        comments={comments}
        sortOrder={commentSortOrder}
        onSortOrderChange={setCommentSortOrder}
        sortValue={commentSortValue}
        onSortValueChange={setCommentSortValue}
        resolutionFilter={commentResolutionFilter}
        onResolutionFilterChange={setCommentResolutionFilter}
        visibilityFilter={commentVisibilityFilter}
        onVisibilityFilterChange={setCommentVisibilityFilter}
        canFilterByVisibility={canFilterByVisibility}
      />

      <CommentsList
        commentsArePending={commentsArePending}
        commentsAreFetching={commentsAreFetching}
        readOnly={readOnly}
        comments={comments}
        resolutionFilter={commentResolutionFilter}
        visibilityFilter={commentVisibilityFilter}
        nodeId={asset?.nodeId}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        scrollContainerRef={scrollContainerRef}
        onScroll={handleScroll}
      />

      {!readOnly && asset?.nodeId && (
        <CommentsComposer nodeId={asset.nodeId} isComposerDisabled={readOnly || !asset?.id} />
      )}
      {enabled && asset?.nodeId && (
        <CommentsChannelSubscription
          nodeId={asset.nodeId}
          hasNextPage={hasNextPage ?? false}
          onNewComment={handleNewComment}
          sortOrder={commentSortOrder}
          sortValue={commentSortValue}
          resolutionFilter={commentResolutionFilter}
          visibilityFilter={commentVisibilityFilter}
          scrollContainerRef={scrollContainerRef}
        />
      )}
    </div>
  );
};
